/**
 * Shared utility for token storage after authentication
 *
 * Centralizes the logic for storing tokens in localStorage and HttpOnly cookies
 * after successful authentication (from Okta or other sources)
 */

const notifyTokenChanged = (): void => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("give-session-token-updated"));
  }
};

/**
 * Removes the client-side session token written before the store-tokens POST.
 * Used when the server rejects the token (401/403) or the request fails:
 * keeping the localStorage token without the httpOnly cookie leaves the app
 * half-authenticated (client GraphQL works, server-side reads don't).
 */
export const rollbackSessionToken = (): void => {
  localStorage.removeItem("give-session-token");
  notifyTokenChanged();
};

/**
 * Handles token storage after login
 * - Clears old tokens from localStorage (except okta- keys)
 * - Clears sessionStorage
 * - Stores new token in localStorage
 * - Sends token to API to store in HttpOnly cookie
 * - Rolls the localStorage token back if the server rejects the POST
 */
export const handleTokenPostLogin = async (accessToken: string) => {
  // Clear all existing tokens and storage - to start afresh
  const keysToRemove = Object.keys(localStorage).filter(
    (key) => !key.includes("okta-"),
  );
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  // check if program id is set in session storage - store this as we may want to reinstate it
  const programId = sessionStorage.getItem("programId");
  sessionStorage.clear();
  // reinstate program id if it was set
  if (programId) {
    sessionStorage.setItem("programId", programId);
  }
  // Store the new access token
  localStorage.setItem("give-session-token", accessToken);
  // Notify same-tab listeners (e.g. useClientGraphqlQuery) that the token changed,
  // so components mounted before login refresh their cached token instead of
  // firing queries with a stale/empty one.
  notifyTokenChanged();
  // Store tokens in memory just long enough to POST them to server
  // Note: This is due to limitation of Okta Sign-In Widget as it uses classic engine atm
  // Ideally, code should be shared on Next.js backend api route and backend api route should be used to store tokens in a cookie
  // instead of us sharing in an API call
  let res: Response;
  try {
    res = await fetch("/api/auth/store-tokens", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        accessToken: accessToken,
      }),
    });
  } catch (err) {
    rollbackSessionToken();
    throw err;
  }
  if (!res.ok) {
    rollbackSessionToken();
  }
  return res;
};
